/**
 * Category Comparison Chart
 * Compares a selected category vs all others year-over-year
 * Dual-axis chart showing funding and deal count
 */

import React, { useMemo, useState } from 'react';
import { withChartConfiguration, ChartProps, EnhancedChartProps } from './withChartConfiguration';
import { formatMonetaryValue, CHART_STYLES } from '../utils/chartUtils';
import {
  categoryComparisonService,
  CategoryComparisonResponse
} from '../services/CategoryComparisonService';
import { Download, FileSpreadsheet, Pencil, Eye, EyeOff } from 'lucide-react';
import { useServiceData } from '../hooks/useChartData';
import { TitleEditor } from './TitleEditor';
import { customTitleService, TitleKey } from '../services/CustomTitleService';
import { ChartMetadata } from './ChartMetadata';
import { BRAND_COLORS, TYPOGRAPHY } from '../types/data';
import { getResponsiveFontSizes, getResponsiveChartHeight } from '../utils/responsiveChartUtils';
import '../styles/chart-components.css';

interface CategoryComparisonChartProps extends ChartProps {
  category: string;
  type: 'product_category' | 'category_domain';
  timeframe: string;
  weekOffset: number;
  filters?: Record<string, string[] | undefined>;
  metadata?: {
    current_period: { start: string; end: string };
    cache_status?: string;
    response_time_ms?: number;
  };
  showLegend?: boolean;
}

type CategoryComparisonChartWrapperProps = Omit<CategoryComparisonChartProps, 'data'>;

/**
 * Configuration function for chart options
 */
const getCategoryComparisonChartOption = (
  props: CategoryComparisonChartProps & EnhancedChartProps
) => {
  const { data, category, type, timeframe, filters, showLegend = true } = props;

  if (!data?.data || !data?.years) {
    return null;
  }

  const comparisonData = data.data;
  const years = data.years;

  // Check for custom title
  const titleKey: TitleKey = {
    chartType: 'category-comparison',
    timeframe,
    filters
  };
  const customTitle = customTitleService.getCustomTitle(titleKey);

  const title = customTitle?.title || `${category} vs All Others`;
  const subtitle = customTitle?.subtitle || 'Year-over-year funding and deal comparison';

  // Prepare series data
  const selectedFunding = years.map((year: string) => comparisonData.selected[year]?.funding || 0);
  const othersFunding = years.map((year: string) => comparisonData.others[year]?.funding || 0);
  const selectedDeals = years.map((year: string) => comparisonData.selected[year]?.deals || 0);
  const othersDeals = years.map((year: string) => comparisonData.others[year]?.deals || 0);

  // Calculate totals for display above bars
  const totalFunding = years.map((year: string, idx: number) =>
    selectedFunding[idx] + othersFunding[idx]
  );

  // Calculate which segments should show labels (only if >15% of total)
  const shouldShowLabel = (value: number, total: number) => {
    return value > 10000000 && (value / total) > 0.15;
  };

  // Data description for Carta-style line
  const dataDescription = `Comparing ${category} funding and deal activity vs all other categories`;

  // Get responsive font sizes for mobile/tablet/desktop
  const fontSizes = getResponsiveFontSizes();

  return {
    title: {
      text: title,
      subtext: subtitle,
      left: '1%',  // Match UnifiedChartFactory standards
      top: '1%',
      textStyle: {
        fontSize: fontSizes.title,  // Responsive: 18px mobile, 24px tablet, 32px desktop
        fontWeight: 700,
        color: BRAND_COLORS.text,
        fontFamily: TYPOGRAPHY.fontFamily
      },
      subtextStyle: {
        fontSize: fontSizes.subtitle,  // Responsive: 14px mobile, 16px tablet, 20px desktop
        fontWeight: 500,
        color: BRAND_COLORS.textSecondary,
        fontFamily: TYPOGRAPHY.fontFamily
      }
    },
    graphic: [
      // CTA separator line
      {
        type: 'line',
        left: '5%',
        right: '5%',
        bottom: '7%',
        shape: {
          x1: 0,
          y1: 0,
          x2: 1,
          y2: 0
        },
        style: {
          stroke: BRAND_COLORS.border,
          lineWidth: 1
        }
      },
      // CTA text
      {
        type: 'text',
        left: 'center',
        bottom: '3%',
        style: {
          text: '{normal|Subscribe for more cybersecurity market intelligence at }{purple|ReturnOnSecurity.com}',
          fontSize: 20,
          fontFamily: TYPOGRAPHY.fontFamily,
          textAlign: 'center',
          rich: {
            normal: {
              fontSize: 20,
              fontWeight: 400,
              fill: BRAND_COLORS.text
            },
            purple: {
              fontSize: 20,
              fontWeight: 700,
              fill: BRAND_COLORS.primary // #633FF3
            }
          }
        }
      }
    ],
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross'
      },
      backgroundColor: BRAND_COLORS.background,
      borderColor: BRAND_COLORS.border,
      borderWidth: 1,
      textStyle: {
        color: BRAND_COLORS.text,
        fontFamily: TYPOGRAPHY.fontFamily
      },
      formatter: (params: any) => {
        const year = params[0].axisValue;
        const selectedData = comparisonData.selected[year];
        const othersData = comparisonData.others[year];
        const breakdownData = comparisonData.breakdown?.[year];

        let html = `<div style="font-weight: 700; font-size: 14px; margin-bottom: 8px; border-bottom: 1px solid ${BRAND_COLORS.border}; padding-bottom: 4px;">${year}</div>`;

        // Overall funding breakdown
        html += `<div style="font-weight: 600; font-size: 12px; margin-top: 6px; margin-bottom: 4px; color: ${BRAND_COLORS.textSecondary};">Overall Funding</div>`;

        const totalFunding = (selectedData?.funding || 0) + (othersData?.funding || 0);
        const items = [
          {
            label: `${category}`,
            value: formatMonetaryValue(selectedData?.funding || 0),
            pct: breakdownData?.totals?.selectedPct || 0,
            color: BRAND_COLORS.primary
          },
          {
            label: 'All Others',
            value: formatMonetaryValue(othersData?.funding || 0),
            pct: breakdownData?.totals?.othersPct || 0,
            color: '#222222'
          }
        ];

        items.forEach(item => {
          html += `
            <div style="margin: 3px 0; display: flex; align-items: center; gap: 8px; font-size: 11px;">
              <span style="display: inline-block; width: 10px; height: 10px; background: ${item.color}; border-radius: 2px; flex-shrink: 0;"></span>
              <span style="font-weight: 500; min-width: 90px;">${item.label}:</span>
              <span style="font-weight: 700;">${item.value}</span>
              <span style="color: ${BRAND_COLORS.textSecondary}; margin-left: auto;">(${item.pct}%)</span>
            </div>
          `;
        });

        // Sub-category breakdown (if available)
        if (breakdownData?.subCategories && Object.keys(breakdownData.subCategories).length > 0) {
          html += `<div style="font-weight: 600; font-size: 12px; margin-top: 8px; margin-bottom: 4px; color: ${BRAND_COLORS.textSecondary}; border-top: 1px solid ${BRAND_COLORS.border}; padding-top: 6px;">${category} Breakdown</div>`;

          Object.entries(breakdownData.subCategories).forEach(([subCat, data]: [string, any]) => {
            const yoyGrowth = data.yoyGrowth;
            const hasYoY = typeof yoyGrowth === 'number';
            const yoyColor = hasYoY ? (yoyGrowth >= 0 ? '#10B981' : '#EF4444') : BRAND_COLORS.textSecondary;
            const yoySymbol = hasYoY ? (yoyGrowth >= 0 ? '↑' : '↓') : '';

            html += `
              <div style="margin: 3px 0; font-size: 11px; padding-left: 18px;">
                <div style="display: flex; align-items: center; gap: 6px;">
                  <span style="font-weight: 500; min-width: 110px;">${subCat}:</span>
                  <span style="font-weight: 700;">${formatMonetaryValue(data.funding)}</span>
                  <span style="color: ${BRAND_COLORS.textSecondary};">(${data.pct}%)</span>
                </div>
                <div style="display: flex; align-items: center; gap: 6px; padding-left: 116px; color: ${BRAND_COLORS.textSecondary};">
                  <span>${data.deals} deals (${data.dealsPct}%)</span>
                  ${hasYoY ? `<span style="color: ${yoyColor}; font-weight: 600; margin-left: auto;">${yoySymbol} ${Math.abs(yoyGrowth).toFixed(1)}% YoY</span>` : ''}
                </div>
              </div>
            `;
          });
        }

        // Deal count summary
        html += `<div style="font-weight: 600; font-size: 12px; margin-top: 8px; margin-bottom: 4px; color: ${BRAND_COLORS.textSecondary}; border-top: 1px solid ${BRAND_COLORS.border}; padding-top: 6px;">Deal Count</div>`;
        const totalDeals = (selectedData?.deals || 0) + (othersData?.deals || 0);
        const dealItems = [
          {
            label: `${category}`,
            value: `${selectedData?.deals || 0} deals`,
            pct: breakdownData?.totals?.selectedDealsPct || 0,
            color: BRAND_COLORS.accent
          },
          {
            label: 'All Others',
            value: `${othersData?.deals || 0} deals`,
            pct: breakdownData?.totals?.othersDealsPct || 0,
            color: BRAND_COLORS.warning
          }
        ];

        dealItems.forEach(item => {
          html += `
            <div style="margin: 3px 0; display: flex; align-items: center; gap: 8px; font-size: 11px;">
              <span style="display: inline-block; width: 10px; height: 10px; background: ${item.color}; border-radius: 2px; flex-shrink: 0;"></span>
              <span style="font-weight: 500; min-width: 90px;">${item.label}:</span>
              <span style="font-weight: 700;">${item.value}</span>
              <span style="color: ${BRAND_COLORS.textSecondary}; margin-left: auto;">(${item.pct}%)</span>
            </div>
          `;
        });

        return html;
      }
    },
    legend: {
      show: showLegend,  // Control legend visibility
      data: [
        `${category} (Funding)`,
        'All Others (Funding)',
        `${category} (Deals)`,
        'All Others (Deals)'
        // 'Total Funding' excluded - it's a hidden helper series for labels
      ],
      bottom: CHART_STYLES.legend.bottom,
      left: CHART_STYLES.legend.alignment,
      textStyle: {
        color: BRAND_COLORS.text,
        fontFamily: TYPOGRAPHY.fontFamily,
        fontSize: CHART_STYLES.legend.fontSize,
        fontWeight: CHART_STYLES.legend.fontWeight
      },
      itemGap: CHART_STYLES.legend.itemGap
    },
    grid: {
      left: CHART_STYLES.grid.left,
      right: '8%',  // Keep wider for dual-axis
      top: CHART_STYLES.grid.top,
      bottom: CHART_STYLES.grid.bottom,
      containLabel: CHART_STYLES.grid.containLabel
    },
    xAxis: {
      type: 'category',
      data: years,
      axisLabel: {
        fontSize: CHART_STYLES.xAxis.fontSize,
        fontWeight: CHART_STYLES.xAxis.fontWeight,
        color: BRAND_COLORS.text,
        fontFamily: TYPOGRAPHY.fontFamily
      },
      axisLine: {
        lineStyle: {
          color: BRAND_COLORS.border
        }
      }
    },
    yAxis: [
      {
        type: 'value',
        position: 'left',
        axisLabel: {
          fontSize: CHART_STYLES.yAxis.fontSize,
          fontWeight: CHART_STYLES.yAxis.fontWeight,
          color: BRAND_COLORS.textSecondary,
          fontFamily: TYPOGRAPHY.fontFamily,
          formatter: (value: number) => {
            if (value >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
            if (value >= 1e6) return `$${(value / 1e6).toFixed(0)}M`;
            if (value >= 1e3) return `$${(value / 1e3).toFixed(0)}K`;
            return `$${value}`;
          }
        },
        splitLine: {
          lineStyle: {
            color: BRAND_COLORS.border,
            type: 'dashed'
          }
        }
      },
      {
        type: 'value',
        position: 'right',
        axisLabel: {
          fontSize: CHART_STYLES.yAxis.fontSize,
          fontWeight: CHART_STYLES.yAxis.fontWeight,
          color: BRAND_COLORS.textSecondary,
          fontFamily: TYPOGRAPHY.fontFamily
        },
        splitLine: {
          show: false
        }
      }
    ],
    series: [
      {
        name: 'All Others (Funding)',
        type: 'bar',
        yAxisIndex: 0,
        stack: 'funding',  // Stack the bars - bottom of stack
        data: othersFunding,
        itemStyle: {
          color: '#222222'  // Dark black per user requirement
        },
        label: {
          show: true,
          position: 'inside',
          formatter: (params: any) => {
            const value = params.value as number;
            const idx = params.dataIndex;
            const total = totalFunding[idx];
            // Only show label if segment is >15% of total and >$10M
            if (!shouldShowLabel(value, total)) return '';
            return formatMonetaryValue(value, 'label');
          },
          fontSize: 14,
          fontWeight: 700,
          color: '#ffffff',
          fontFamily: TYPOGRAPHY.fontFamily
        },
        emphasis: {
          focus: 'series'
        }
      },
      {
        name: `${category} (Funding)`,
        type: 'bar',
        yAxisIndex: 0,
        stack: 'funding',  // Stack with all others - top of stack for visibility
        data: selectedFunding,
        itemStyle: {
          color: BRAND_COLORS.primary
        },
        label: {
          show: true,
          position: 'inside',
          formatter: (params: any) => {
            const value = params.value as number;
            const idx = params.dataIndex;
            const total = totalFunding[idx];
            // Only show label if segment is >15% of total and >$10M
            if (!shouldShowLabel(value, total)) return '';
            return formatMonetaryValue(value, 'label');
          },
          fontSize: 14,
          fontWeight: 700,
          color: '#ffffff',
          fontFamily: TYPOGRAPHY.fontFamily
        },
        emphasis: {
          focus: 'series'
        }
      },
      {
        name: `${category} (Deals)`,
        type: 'line',
        yAxisIndex: 1,
        data: selectedDeals,
        itemStyle: {
          color: BRAND_COLORS.accent
        },
        lineStyle: {
          width: 3
        },
        label: {
          show: true,
          formatter: '{c}',
          fontSize: 14,
          fontWeight: 600,
          color: BRAND_COLORS.accent,
          fontFamily: TYPOGRAPHY.fontFamily,
          position: 'top'
        },
        emphasis: {
          focus: 'series'
        }
      },
      {
        name: 'All Others (Deals)',
        type: 'line',
        yAxisIndex: 1,
        data: othersDeals,
        itemStyle: {
          color: BRAND_COLORS.warning
        },
        lineStyle: {
          width: 3,
          type: 'dashed'
        },
        label: {
          show: true,
          formatter: '{c}',
          fontSize: 14,
          fontWeight: 600,
          color: BRAND_COLORS.warning,
          fontFamily: TYPOGRAPHY.fontFamily,
          position: 'top'
        },
        emphasis: {
          focus: 'series'
        }
      },
      // Hidden series to show total labels above stacked bars
      {
        name: 'Total Funding',
        type: 'bar',
        yAxisIndex: 0,
        data: totalFunding,  // Use actual totals for positioning
        itemStyle: {
          color: 'transparent',  // Invisible bars
          borderColor: 'transparent'
        },
        barGap: '-100%',  // Overlay on the stacked bars
        label: {
          show: true,
          position: 'top',  // Position above the bar
          formatter: (params: any) => formatMonetaryValue(params.value, 'label'),
          fontSize: 16,
          fontWeight: 700,
          color: BRAND_COLORS.text,
          fontFamily: TYPOGRAPHY.fontFamily,
          distance: 5  // Small gap above the bar
        },
        tooltip: {
          show: false  // Don't show in tooltip
        },
        silent: true,  // Don't respond to mouse events
        legendHoverLink: false,  // Don't interact with legend
        emphasis: {
          disabled: true  // Disable emphasis effect
        }
      }
    ]
  };
};

/**
 * Base chart component that receives data and renders
 */
const CategoryComparisonChartBase: React.FC<
  CategoryComparisonChartProps & EnhancedChartProps
> = React.memo(({
  data,
  category,
  type,
  timeframe,
  filters,
  chartRef,
  isLoading,
  error,
  downloadChart,
  refreshChart,
  metadata,
  ...restProps
}) => {
  const [editorOpen, setEditorOpen] = useState(false);
  const [forceRefresh, setForceRefresh] = useState(0);
  const [showLegend, setShowLegend] = useState(true);

  // Generate title key
  const titleKey: TitleKey = React.useMemo(
    () => ({
      chartType: 'category-comparison',
      timeframe,
      filters
    }),
    [timeframe, filters]
  );

  // Check for custom title
  const customTitle = React.useMemo(
    () => customTitleService.getCustomTitle(titleKey),
    [titleKey, forceRefresh]
  );

  // Refresh chart when forceRefresh changes
  React.useEffect(() => {
    if (forceRefresh > 0) {
      refreshChart();
    }
  }, [forceRefresh, refreshChart]);

  // Refresh chart when legend visibility changes
  React.useEffect(() => {
    refreshChart();
  }, [showLegend, refreshChart]);

  // Get chart option for title extraction
  const chartOption = React.useMemo(
    () =>
      getCategoryComparisonChartOption({
        data,
        category,
        type,
        timeframe,
        filters,
        showLegend
      } as CategoryComparisonChartProps & EnhancedChartProps),
    [data, category, type, timeframe, filters, showLegend, forceRefresh]
  );

  const titleOption = Array.isArray(chartOption?.title)
    ? chartOption.title[0]
    : chartOption?.title;
  const currentTitle = titleOption?.text || '';
  const currentSubtitle = titleOption?.subtext || '';

  const hasData = data?.data && data?.years && data.years.length > 0;

  if (error) {
    return (
      <div className="chart-container-error">
        <div className="chart-error">
          <p className="chart-error-title">Error loading category comparison</p>
          <p className="chart-error-message">{error.message}</p>
        </div>
      </div>
    );
  }

  if (!hasData && !isLoading) {
    return (
      <div className="chart-container">
        <div className="chart-header">
          <h3 className="chart-title">Category Comparison</h3>
        </div>
        <div className="chart-empty">
          <p className="chart-empty-message">
            No comparison data available for this category
          </p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="chart-container-loading">
        <div className="chart-loading">
          <div className="chart-loading-spinner"></div>
        </div>
      </div>
    );
  }

  const handleCSVExport = () => {
    if (!data?.data || !data?.years) return;

    const headers = [
      'Year',
      `${category} Funding`,
      `${category} Deals`,
      'All Others Funding',
      'All Others Deals'
    ];

    const rows = data.years.map((year: string) => {
      const selectedData = data.data.selected[year];
      const othersData = data.data.others[year];

      return [
        year,
        selectedData?.funding || 0,
        selectedData?.deals || 0,
        othersData?.funding || 0,
        othersData?.deals || 0
      ];
    });

    const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    // Generate clean filename: category-first, no spaces, simple format
    const cleanCategory = category.toLowerCase().replace(/\s+/g, '-');
    const dateStr = new Date().toISOString().split('T')[0];

    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `${cleanCategory}-comparison-${dateStr}.csv`
    );
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <div className="mt-8 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 relative">
        {/* Action buttons - hidden on mobile for less crowding */}
        <div className="hidden sm:flex absolute top-4 right-4 z-10 gap-1.5">
          <button
            onClick={() => setEditorOpen(true)}
            className="p-1.5 bg-white dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 rounded shadow-sm border border-gray-300 dark:border-gray-600 transition-colors group"
            title="Edit chart title"
          >
            <Pencil className="w-3 h-3 text-gray-600 dark:text-gray-300 group-hover:text-purple-600 dark:group-hover:text-purple-400" />
            {customTitle && (
              <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-purple-600 rounded-full border border-white dark:border-gray-800" />
            )}
          </button>
          <button
            onClick={() => setShowLegend(!showLegend)}
            className="chart-button chart-button-secondary text-xs px-2 py-1.5"
            title={showLegend ? "Hide legend" : "Show legend"}
            disabled={isLoading}
          >
            {showLegend ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
          </button>
          <button
            onClick={handleCSVExport}
            className="chart-button chart-button-secondary text-xs px-2 py-1.5"
            title="Export data as CSV"
            disabled={isLoading}
          >
            <FileSpreadsheet className="w-3 h-3" />
            CSV
          </button>
          <button
            onClick={() => downloadChart('blog')}
            className="chart-button chart-button-primary text-xs px-2 py-1.5"
            title="Download chart as PNG"
            disabled={isLoading}
          >
            <Download className="w-3 h-3" />
            PNG
          </button>
        </div>

        {/* Chart canvas */}
        <div
          ref={chartRef}
          className="w-full"
          style={{
            height: `${getResponsiveChartHeight(700, 'category')}px`,  // Responsive: 500px mobile, 560px tablet, 700px desktop
            minHeight: `${getResponsiveChartHeight(700, 'category')}px`
          }}
        />

        {/* Chart Metadata */}
        {metadata && (
          <ChartMetadata
            periodStart={metadata.current_period?.start}
            periodEnd={metadata.current_period?.end}
            cacheStatus={metadata.cache_status}
            responseTimeMs={metadata.response_time_ms}
          />
        )}
      </div>

      {/* Title Editor Modal */}
      <TitleEditor
        isOpen={editorOpen}
        onClose={() => setEditorOpen(false)}
        currentTitle={currentTitle}
        currentSubtitle={currentSubtitle}
        onSave={(title, subtitle) => {
          customTitleService.setCustomTitle(titleKey, title, subtitle);
          setForceRefresh(prev => prev + 1);
          setEditorOpen(false);
        }}
        onReset={() => {
          customTitleService.resetCustomTitle(titleKey);
          setForceRefresh(prev => prev + 1);
          setEditorOpen(false);
        }}
        chartContext={{
          chartType: 'category-comparison',
          timeframe,
          category
        }}
      />
    </>
  );
});

/**
 * Data fetching wrapper component
 */
const CategoryComparisonChartWithData: React.FC<CategoryComparisonChartWrapperProps> = ({
  category,
  type,
  timeframe,
  weekOffset,
  filters,
  metadata
}) => {
  const { data, isLoading, error } = useServiceData(
    () =>
      categoryComparisonService.getCategoryComparison(
        category,
        type,
        timeframe,
        weekOffset,
        filters
      ),
    [category, type, timeframe, weekOffset, filters],
    {
      validationEnabled: false,
      retryOnError: true,
      maxRetries: 3
    }
  );

  const chartMetadata = data
    ? {
        current_period: data.metadata?.current_period || { start: '', end: '' },
        cache_status: data.cache_status,
        response_time_ms: data.response_time_ms
      }
    : undefined;

  return (
    <CategoryComparisonChartWithHOC
      data={data}
      category={category}
      type={type}
      timeframe={timeframe}
      weekOffset={weekOffset}
      filters={filters}
      metadata={chartMetadata}
      isLoading={isLoading}
      error={error}
    />
  );
};

// Create the HOC-wrapped component
const CategoryComparisonChartWithHOC = withChartConfiguration(
  CategoryComparisonChartBase,
  getCategoryComparisonChartOption
);

export default CategoryComparisonChartWithData;
